/**
 * referenceSlice — Vinculação de referências (works) ao projeto ativo (add/remove/load com cache)
 *
 * Consumidores: Citations, Conexoes, DadosPesquisa, ExploracaoIA, GlobalInspector, +5 componentes (via driveProjectStore)
 * Dependências: googleDrive, helpers, projectCache
 * Persistência: Google Drive (referencias.json por projeto) + IndexedDB (stale-while-revalidate)
 */
import { googleDrive } from '../../services/googleDrive';
import { logger } from '../../utils/logger';
import { driveErrorToast } from '../../utils/driveErrorToast';
import { auditLog } from '../../utils/auditLog';
import { ensureAuthenticated } from './helpers';
import { refsCache, getCacheKey } from './projectCache';
// syncUserStats lazy-loaded to keep @supabase out of entry chunk (#35)
import type { SliceSet, SliceGet } from './types';

// Debounce flag for refs stats sync (once per session)
let _refsSynced = false;

export const createReferenceSlice = (set: SliceSet, get: SliceGet) => {

  // Timestamp da última mutação local (optimistic update).
  // Usado pelo _backgroundRevalidate para evitar sobrescrever dado local
  // mais recente com dado stale vindo do Drive.
  let _lastMutationAt = 0;

  // Background revalidation: busca dados frescos do Drive sem bloquear a UI.
  // Extraído como função nomeada para reutilização pelo guard de TTL e pelo
  // visibilitychange (via revalidateReferencesIfStale).
  const _backgroundRevalidate = (projectId: string, cacheKey: string) => {
    // Guard: se já há revalidação em andamento, não duplicar
    if (get().isLoadingReferences) return;
    set({ isLoadingReferences: true });

    const startedAt = Date.now();

    ensureAuthenticated().then(() =>
      googleDrive.getProjectReferences(projectId)
    ).then(freshRefs => {
      // Guard: só atualiza se o projeto ativo não mudou durante o fetch
      if (get().activeProjectId !== projectId) return;

      // Guard: se houve mutação local APÓS o início desta revalidação,
      // descartar a resposta do Drive para não sobrescrever dado mais recente.
      // A próxima revalidação (após TTL) trará os dados já sincronizados.
      if (_lastMutationAt > startedAt) {
        set({ isLoadingReferences: false });
        logger.info('referenceSlice', 'Revalidação descartada — mutação local mais recente');
        return;
      }

      if (freshRefs !== null) {
        set({ cachedReferences: freshRefs, referencesLoadedAt: Date.now(), isLoadingReferences: false });
        refsCache.set(cacheKey, freshRefs);
      } else {
        // Drive retornou null (erro tratado internamente) — manter cache atual
        set({ isLoadingReferences: false });
      }
    }).catch((err) => {
      if (get().activeProjectId === projectId) {
        set({ isLoadingReferences: false });
      }
      logger.error('[referenceSlice] Revalidação em background falhou:', String(err));
    });
  };

  return {
  // Carrega referências do projeto ativo com stale-while-revalidate
  loadActiveProjectReferences: async () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      return [];
    }

    // Se já carregou E dados são frescos (< 5 min), retorna do Zustand sem fetch
    const loadedAt = get().referencesLoadedAt;
    if (get().referencesLoaded && loadedAt && Date.now() - loadedAt < 300000) {
      return get().cachedReferences || [];
    }

    // Se já carregou MAS dados estão stale (≥ 5 min): serve cache instantâneo + revalida em background
    if (get().referencesLoaded) {
      const cacheKey = getCacheKey('refs', activeProjectId);
      _backgroundRevalidate(activeProjectId, cacheKey);
      return get().cachedReferences || [];
    }

    // Se está carregando, não iniciar nova chamada
    if (get().isLoadingReferences) {
      return get().cachedReferences || [];
    }

    const cacheKey = getCacheKey('refs', activeProjectId);

    // 1. Tentar IndexedDB (instantâneo)
    try {
      const cached = await refsCache.get(cacheKey);
      if (cached.success && cached.data !== null) {
        // Serve stale imediatamente
        set({ cachedReferences: cached.data, referencesLoaded: true, referencesLoadedAt: Date.now(), isLoadingReferences: true });

        // 2. Revalidar em background (silencioso)
        const firstLoadStartedAt = Date.now();
        ensureAuthenticated().then(() =>
          googleDrive.getProjectReferences(activeProjectId)
        ).then(freshRefs => {
          // Guard: só atualiza se o projeto ativo não mudou durante o fetch
          if (get().activeProjectId !== activeProjectId) return;
          // Guard: se houve mutação local após início do fetch, descartar resposta stale
          if (_lastMutationAt > firstLoadStartedAt) {
            set({ isLoadingReferences: false });
            return;
          }
          if (freshRefs !== null) {
            set({ cachedReferences: freshRefs, referencesLoadedAt: Date.now(), isLoadingReferences: false });
            refsCache.set(cacheKey, freshRefs);
          } else {
            // Drive retornou null (erro tratado internamente) — manter cache atual
            set({ isLoadingReferences: false });
          }
        }).catch((err) => {
          if (get().activeProjectId === activeProjectId) {
            set({ isLoadingReferences: false });
          }
          logger.error('[referenceSlice] Revalidação falhou:', String(err));
        });

        return cached.data;
      }
    } catch {
      // IndexedDB indisponível — fallback para Drive
    }

    // 3. Sem cache: buscar do Drive (mostra skeleton)
    set({ isLoadingReferences: true });

    try {
      await ensureAuthenticated();
      const refs = await googleDrive.getProjectReferences(activeProjectId);
      const data = refs || [];
      set({ cachedReferences: data, referencesLoaded: true, referencesLoadedAt: Date.now(), isLoadingReferences: false });

      // Salvar no IndexedDB para próxima abertura
      refsCache.set(cacheKey, data);

      // Sync ref count to Supabase (once per session)
      if (!_refsSynced && data.length > 0) {
        _refsSynced = true;
        try {
          const userStr = localStorage.getItem('bigwa_user');
          if (userStr) {
            const userData = JSON.parse(userStr);
            if (userData?.email) {
              const projectCount = (get().projects || []).filter(p => p.status === 'active').length;
              import('../../lib/supabaseClient').then(m => m.syncUserStats(userData.email, data.length, projectCount)).catch(() => {});
            }
          }
        } catch {
          // Ignore
        }
      }

      return data;
    } catch (error) {
      logger.error('[referenceSlice.loadActiveProjectReferences]', String(error));
      set({ cachedReferences: [], referencesLoaded: true, referencesLoadedAt: Date.now(), isLoadingReferences: false });
      return [];
    }
  },

  // Adiciona referência ao projeto ativo (optimistic: UI primeiro, Drive em background)
  addReferenceToActiveProject: async (workId: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // 1. Salva estado anterior para rollback
    const previousRefs = get().cachedReferences || [];

    // 2. Adiciona ao cache local IMEDIATAMENTE
    const newRef = {
      workId,
      archetype: null,
      readStatus: 'unread',
      order: previousRefs.length,
      addedAt: new Date().toISOString(),
      relevance: null,
      integrationTags: [],
      isFavorite: false,
    };
    set(state => ({ cachedReferences: [...(state.cachedReferences || []), newRef] }));
    _lastMutationAt = Date.now();

    // 3. Persiste no IndexedDB (rápido, local)
    const cacheKey = getCacheKey('refs', activeProjectId);
    refsCache.set(cacheKey, get().cachedReferences || []);

    // 4. Persiste no Drive em background (com rollback se falhar)
    try {
      await ensureAuthenticated();
      await googleDrive.addReferenceToProject(activeProjectId, workId);
      set({ referencesLoadedAt: Date.now() });
      auditLog({ operation: 'create', target: 'referência-projeto', targetId: workId, detail: `projeto ${activeProjectId}`, success: true });
    } catch (error) {
      set({ cachedReferences: previousRefs });
      refsCache.set(cacheKey, previousRefs);
      driveErrorToast(error, 'Não foi possível adicionar a referência ao projeto. Tente novamente.');
      auditLog({ operation: 'create', target: 'referência-projeto', targetId: workId, detail: `projeto ${activeProjectId}`, success: false, error: String(error) });
      throw error;
    }
  },

  // Adiciona referência a qualquer mesa (write direto ao Drive, sem optimistic update)
  // Usado pelo split button quando o destino NÃO é a mesa ativa
  addReferenceToProject: async (workId: string, targetProjectId: string) => {
    await ensureAuthenticated();

    try {
      await googleDrive.addReferenceToProject(targetProjectId, workId);
      auditLog({ operation: 'create', target: 'referência-projeto', targetId: workId, detail: `projeto ${targetProjectId}`, success: true });
    } catch (error) {
      // "already exists" → re-throw para o caller (hook) tratar contagem
      if (error instanceof Error && error.message === 'Referência já está no projeto') {
        throw error;
      }

      // Erro real: log + toast + audit (sem rollback — não há state local)
      driveErrorToast(error, 'Não foi possível adicionar a referência ao projeto. Tente novamente.');
      auditLog({ operation: 'create', target: 'referência-projeto', targetId: workId, detail: `projeto ${targetProjectId} — ${String(error)}`, success: false, error: String(error) });
      throw error;
    }
  },

  // Remove referência do projeto ativo (optimistic: UI primeiro, Drive em background)
  removeReferenceFromActiveProject: async (workId: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // 1. Salva estado anterior para rollback
    const previousRefs = get().cachedReferences || [];

    // 2. Remove do cache local IMEDIATAMENTE
    set(state => ({
      cachedReferences: (state.cachedReferences || []).filter(r => r.workId !== workId)
    }));
    _lastMutationAt = Date.now();

    // 3. Persiste no IndexedDB (rápido, local)
    const cacheKey = getCacheKey('refs', activeProjectId);
    refsCache.set(cacheKey, get().cachedReferences || []);

    // 4. Persiste no Drive em background (com rollback se falhar)
    try {
      await ensureAuthenticated();
      await googleDrive.removeReferenceFromProject(activeProjectId, workId);
      set({ referencesLoadedAt: Date.now() });
      auditLog({ operation: 'delete', target: 'referência-projeto', targetId: workId, detail: `projeto ${activeProjectId}`, success: true });
    } catch (error) {
      set({ cachedReferences: previousRefs });
      refsCache.set(cacheKey, previousRefs);
      driveErrorToast(error, 'Não foi possível remover a referência do projeto. Tente novamente.');
      auditLog({ operation: 'delete', target: 'referência-projeto', targetId: workId, detail: `projeto ${activeProjectId}`, success: false, error: String(error) });
      throw error;
    }
  },

  // Obtém referências do projeto ativo (usa cache)
  getActiveProjectReferences: async () => {
    return get().loadActiveProjectReferences();
  },

  // Retorna referências do cache (síncrono)
  getCachedReferences: () => {
    return get().cachedReferences || [];
  },

  // Atualiza referência no projeto ativo (optimistic: UI primeiro, Drive em background)
  updateReferenceInActiveProject: async (workId: string, updates: {
    archetype?: string | null;
    readStatus?: string;
    order?: number;
    relevance?: string | null;
    integrationTags?: string[];
    isFavorite?: boolean;
    status?: 'pending' | 'analyzing' | 'analyzed' | null;
    isCited?: boolean;
    isFlagged?: boolean;
    classifiedWithAI?: boolean;
    cardColor?: string;
    stickyNote?: string;
    highlightCount?: number;
    lastPage?: number;
  }) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Guard: skip if reference doesn't exist in cache (avoids state oscillation loop)
    const currentRefs = get().cachedReferences || [];
    if (!currentRefs.some(ref => ref.workId === workId)) {
      return;
    }

    // 1. Salva estado anterior para rollback
    const previousRefs = currentRefs;

    // 2. Atualiza cache local IMEDIATAMENTE (UI instantâneo)
    set(state => ({
      cachedReferences: (state.cachedReferences || []).map(ref =>
        ref.workId === workId ? { ...ref, ...updates } : ref
      )
    }));
    _lastMutationAt = Date.now();

    // 3. Persiste no IndexedDB (rápido, local)
    const cacheKey = getCacheKey('refs', activeProjectId);
    refsCache.set(cacheKey, get().cachedReferences || []);

    // 4. Persiste no Drive em background (com rollback se falhar)
    try {
      await ensureAuthenticated();
      await googleDrive.updateProjectReference(activeProjectId, workId, updates);
      set({ referencesLoadedAt: Date.now() });
      auditLog({ operation: 'update', target: 'referência-projeto', targetId: workId, detail: `projeto ${activeProjectId}`, success: true });
    } catch (error) {
      // Rollback: restaura estado anterior
      set({ cachedReferences: previousRefs });
      refsCache.set(cacheKey, previousRefs);
      driveErrorToast(error, 'Não foi possível atualizar a referência. Tente novamente.');
      auditLog({ operation: 'update', target: 'referência-projeto', targetId: workId, detail: `projeto ${activeProjectId}`, success: false, error: String(error) });
      throw error;
    }
  },

  // Invalida o cache de referências (força recarregar na próxima vez)
  invalidateReferencesCache: () => {
    const activeProjectId = get().activeProjectId;
    set({ cachedReferences: [], referencesLoaded: false, referencesLoadedAt: null });
    if (activeProjectId) {
      refsCache.delete(getCacheKey('refs', activeProjectId));
    }
  },

  // Revalida referências em background SE dados estão stale (≥ 5 min).
  // Chamado pelo visibilitychange do DashboardLayout para sync cross-device.
  // Retorna instantaneamente — nunca bloqueia UI.
  revalidateReferencesIfStale: () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) return;
    if (!get().referencesLoaded) return;

    const loadedAt = get().referencesLoadedAt;
    // Dados frescos (< 5 min) — nada a fazer
    if (loadedAt && Date.now() - loadedAt < 300000) return;

    const cacheKey = getCacheKey('refs', activeProjectId);
    _backgroundRevalidate(activeProjectId, cacheKey);
  },
};
};
